import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {getUser} from "../../utils/user";
import {hasUser} from "../../utils/cookies";
import './email_notification.scss';
import useNotification from "../notification/notification";
import Button from "@mui/material/Button/Button";
import {resendConfirmEmail} from "../../redux/user/requests";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {useSnackbar} from "notistack";
import {useLocation} from "react-router-dom";

const NOTIFICATION_DELAY = 500;
const AUTOHIDE = 500000;

let snackbarKey = null;
const EmailVerificationNotification = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const location = useLocation();
    const logged = hasUser();
    const [, sendNotification] = useNotification();

    // Keep the latest session locally and update when the app dispatches "user_refreshed"
    const [session, setSession] = useState(() => getUser());
    const snackbarKeyRef = useRef(null);
    const [notificationDisplayed, setNotificationDisplayed] = useState(false);

    // Normalize “excluded” routes as prefixes (no need to duplicate with/without trailing slash)
    const excludedPrefixes = useMemo(
        () => [
            "/",
            "/subscriptions",
            "/confirm-email",
            "/get-started",
            "/confirm-free",
            "/logout",
            "/voucher",
            "/terms-of-use",
            "/privacy-policy",
            "/home",
            "/about-us",
        ],
        []
    );

    const isExcludedPath = useCallback(
        (path) => {
            const clean = (path || "/").replace(/\/+$/, "") || "/";
            return excludedPrefixes.some((p) => p === clean || clean.startsWith(p + "/"));
        },
        [excludedPrefixes]
    );

    const requireUX = session?.require_email_verification_for_ux !== false; // default ON
    const emailConfirmed = !!session?.email_confirmed;

    const MESSAGE = (
        <p className="color-white margin-left-20 text-center margin-bottom-0 margin-top-0">
            Please verify your email address.
            <br />
            Didn't get the email?
            <br />
            <Button variant="text" color="white" onClick={() => resendEmail()} className="color-white" size="xs">
                Resend Verification
            </Button>
        </p>
    );

    const action = (key) => (
        <Fragment>
            <IconButton onClick={() => closeSnackbar(key)} style={{ color: "white" }}>
                <CloseIcon />
            </IconButton>
        </Fragment>
    );

    const displayEmailNotification = useCallback(() => {
        if (notificationDisplayed) return; // already visible
        snackbarKeyRef.current = enqueueSnackbar(MESSAGE, {
            variant: "warning",
            autoHideDuration: AUTOHIDE,
            preventDuplicate: true,
            action,
        });
        setNotificationDisplayed(true);
    }, [enqueueSnackbar, MESSAGE, action, notificationDisplayed]);

    const hideEmailNotification = useCallback(() => {
        if (snackbarKeyRef.current) {
            closeSnackbar(snackbarKeyRef.current);
            snackbarKeyRef.current = null;
        }
        if (notificationDisplayed) setNotificationDisplayed(false);
    }, [closeSnackbar, notificationDisplayed]);

    const resendEmail = useCallback(() => {
        hideEmailNotification();
        resendConfirmEmail()
            .then((r) => {
                if (r.data?.success) {
                    sendNotification({ msg: "Email sent!", variant: "success" });
                } else {
                    sendNotification({
                        msg: "An error occurred, please try again later or contact system admin",
                        variant: "error",
                        autoHideDuration: 3000,
                    });
                    setTimeout(() => displayEmailNotification(), NOTIFICATION_DELAY);
                }
            })
            .catch(() => {
                sendNotification({
                    msg: "An error occurred, please try again later or contact system admin",
                    variant: "error",
                    autoHideDuration: 3000,
                });
                setTimeout(() => displayEmailNotification(), NOTIFICATION_DELAY);
            });
    }, [hideEmailNotification, sendNotification, displayEmailNotification]);

    // Re-evaluate on route change or when session/flags change
    useEffect(() => {
        const shouldShow =
            logged && requireUX && session && !emailConfirmed && !isExcludedPath(location.pathname);

        if (shouldShow) {
            displayEmailNotification();
        } else {
            hideEmailNotification();
        }
    }, [logged, session, requireUX, emailConfirmed, location.pathname, isExcludedPath, displayEmailNotification, hideEmailNotification]);


    // Listen for “user_refreshed” to update local session (e.g., after email confirm or logout)
    useEffect(() => {
        const handleUserRefreshed = () => {
            const updated = getUser();
            setSession(updated || null);
            // If backend disabled the UX requirement, close immediately
            if (updated && updated.require_email_verification_for_ux === false) {
                hideEmailNotification();
            }
        };
        document.addEventListener("user_refreshed", handleUserRefreshed);
        return () => document.removeEventListener("user_refreshed", handleUserRefreshed);
    }, [hideEmailNotification]);

    return null;
}

export default EmailVerificationNotification;
